import React from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import matchSorter from 'match-sorter';
import { TextField, TextFieldProps } from '@ha/dsl';
import { Country } from './types';
import CountryCodeOption from './CountryCodeOption';

interface Props {
  onCodeChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    values: Country,
  ) => void;
  countries: Country[];
  countryCode: string;
  selectPlaceholder: string;
}

const useStyles = makeStyles({
  option: {
    fontSize: 15,
    '& > span': {
      marginRight: 10,
      fontSize: 18,
    },
  },
  input: {
    textOverflow: 'clip',
  },
});

const filterOptions = (
  options: Country[],
  { inputValue }: { inputValue: string },
) =>
  matchSorter(options, inputValue, {
    keys: [
      { threshold: matchSorter.rankings.STARTS_WITH, key: 'name' },
      { threshold: matchSorter.rankings.STARTS_WITH, key: 'code' },
      'dialCode',
    ],
  });

const CountrySelect: React.FC<Props & TextFieldProps> = ({
  onCodeChange,
  countries,
  countryCode,
  selectPlaceholder,
  inputProps,
}) => {
  const classes = useStyles();

  const findCountry = (code: string) => {
    return countries.find(ctry => ctry.code === code);
  };

  const getFormattedValue = (code: string) => {
    const country = findCountry(code);

    return country ? `${country.code}, ${country.dialCode}` : '';
  };

  const options = [...countries].sort((a, b) => a.code.localeCompare(b.code));

  return (
    <Autocomplete
      options={options}
      classes={{
        option: classes.option,
        input: classes.input,
      }}
      onChange={onCodeChange}
      filterOptions={filterOptions}
      autoHighlight
      defaultValue={findCountry(countryCode)}
      getOptionLabel={option => getFormattedValue(option.code || countryCode)}
      renderOption={option => <CountryCodeOption option={option} />}
      renderInput={params => (
        <TextField
          {...params}
          label={selectPlaceholder}
          variant="standard"
          fullWidth
          inputProps={{
            ...params.inputProps,
            ...inputProps,
            autoComplete: 'off',
          }}
        />
      )}
    />
  );
};

export default CountrySelect;
